import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dropdown from 'react-dropdown';
import intersection from 'lodash.intersection';
import get from 'lodash.get';
import values from 'lodash.values';
import flatten from 'lodash.flatten';
import classNames from 'classnames';
import 'react-dropdown/style.css';
import {
    PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION,
    MEASURE_BY_SKILL_NAME,
    MEASURE_NAMES,
} from '../../../constants/assessments';
import { BURST, DIBELS8, R3D, R3DSPANISH, IDEL2, ESPANOL } from '../../../constants/applications';
import PMMeasuresDropdownItem from './PMMeasuresDropdownItem';
import {
    getRawForms,
    getGroupedForms,
    getSortedGrades,
} from '../../../common/utils/prepareFormsData';
import { useAppContext } from '../../../common/contexts/AppContext';
import { usePopupContext } from '../../../common/contexts/PopupContext';
import { studentType } from '../../../constants/prop-types';
import { navigateToPath } from '../../../common/navigation/navigation';
import { getClosestAvailableFormGrade } from '../../../common/recommendation-rules/getNextForm';
import { localize } from '../../../common/localization';

import { openOnEnterKeyPressedOnDropdown } from '../../../common/utils/dropDownHelpers';

const DROPDOWN_NAME = 'All measures';
const DROPDOWN_PLACEHOLDER = 'Select a measure';

// defines order of application groups in dropdown
const ORDERED_APPS = [DIBELS8, R3D, BURST, IDEL2, R3DSPANISH];
// Dropdown keys structure is a hack that is needed to store
// pair {skillName, measureName} in react dropdown.
// We can't store entire {skillName, measureName} object in
// dropdown options as it causes `Encountered two children with the same key`
// warning during runtime.
// (https://github.com/fraserxu/react-dropdown/blob/master/index.js#L120)
// So string key in PM_DROPDOWN_KEYS object is used to
// generate dropdown list key and further access to real
// skill and measure name.
const PM_DROPDOWN_KEYS = Object.keys(PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION).reduce(
    (dropdownKeys, applicationName) => {
        const appKeys = PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION[applicationName].reduce(
            (keys, { skillName, measureName }) => ({
                ...keys,
                [`${skillName}-${measureName}`]: { skillName, measureName },
            }),
            {},
        );
        return {
            ...dropdownKeys,
            ...appKeys,
        };
    },
    {},
);

const getDropdownItems = (skillAndMeasureNames, skillStatuses) =>
    skillAndMeasureNames
        .filter(({ measureName }) => measureName !== MEASURE_NAMES.CP) // we do not need to show CLPL in PM dropdown
        .reduce((allDropdownItems, { skillName, measureName }) => {
            const { label } = skillStatuses.find(
                s => s.skillName === skillName && s.measureName === measureName,
            );
            return [
                ...allDropdownItems,
                {
                    value: `${skillName}-${measureName}`,
                    label: (
                        <PMMeasuresDropdownItem
                            skillName={skillName}
                            measureName={measureName}
                            label={label}
                        />
                    ),
                },
            ];
        }, []);

const getDropdownOptions = appSkillStatuses =>
    intersection(ORDERED_APPS, Object.keys(appSkillStatuses)).map(applicationName => ({
        type: 'group',
        name: `group-${applicationName}`,
        items: getDropdownItems(
            PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION[applicationName],
            appSkillStatuses[applicationName],
        ),
    }));

// this Map is used to store ref for all dropdowns for each student in PM view
const refCollection = new Map();

const PMMeasures = ({
    student,
    skillsStatusesByApplication,
    nextFormUids,
    hasInterventionInstruction,
}) => {
    const { forms, selectedAppGroup } = useAppContext();
    const {
        actions: { showPMFormSelectionPopup },
    } = usePopupContext();
    const rawForms = getRawForms(forms);

    refCollection.set(student, useRef(null));

    const isDevEnv = process.env.NODE_ENV === 'development';
    const dashboardPrefix = isDevEnv ? '' : 'dashboard-';

    const dropdownsSelector = `.${dashboardPrefix}additional-measures-container .Dropdown-root`;

    const dropdowns = document.querySelectorAll(dropdownsSelector);

    useEffect(() => {
        if (dropdowns.length !== 0) {
            dropdowns.forEach(dropdown => {
                dropdown.setAttribute('tabindex', '0');
                dropdown.addEventListener(
                    'keydown',
                    openOnEnterKeyPressedOnDropdown(refCollection.get(student), dropdown),
                );
            });
        }

        return () => {
            if (dropdowns.length !== 0) {
                dropdowns.forEach(dropdown => {
                    dropdown.setAttribute('tabindex', '0');
                    dropdown.addEventListener(
                        'keydown',
                        openOnEnterKeyPressedOnDropdown(refCollection.get(student), dropdown),
                    );
                });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdowns]);

    const getRecommendedGrade = formsBySkill => {
        // Return the first from assessment available grades if student grade is out of range
        const { grade: studentGrade } = student;
        const hasFormsForStudentGrade = Object.keys(formsBySkill).some(
            formGrade => studentGrade === formGrade,
        );
        return hasFormsForStudentGrade ? studentGrade : getSortedGrades(formsBySkill)[0];
    };

    const getBurstRecommendedGrade = formsBySkill => {
        const { grade: studentGrade } = student;
        return getClosestAvailableFormGrade(formsBySkill, studentGrade);
    };

    const prepareDataForPopup = (application, skillName, measureName) => {
        const formBySkill = getGroupedForms(rawForms, skillName, measureName);
        const grade = hasInterventionInstruction
            ? getBurstRecommendedGrade(formBySkill)
            : getRecommendedGrade(formBySkill);
        const measure = measureName || MEASURE_BY_SKILL_NAME[skillName];
        const initialFormUidsByGrade = get(nextFormUids, `${application}.${measure}`, {});

        showPMFormSelectionPopup({
            studentId: student.id,
            skillName,
            measureName,
            grade,
            initialFormUidsByGrade,
        });
    };

    const measureAndSkillInApplication = (appName, selectedSkillName, selectedMeasureName) =>
        PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION[appName].some(
            ({ measureName, skillName }) =>
                measureName === selectedMeasureName && skillName === selectedSkillName,
        );

    const onSkillNameSelected = event => {
        const { skillName: selectedSkillName, measureName: selectedMeasureName } = get(
            PM_DROPDOWN_KEYS,
            event.value,
            {},
        );
        if (measureAndSkillInApplication(DIBELS8, selectedSkillName, selectedMeasureName)) {
            prepareDataForPopup(DIBELS8, selectedSkillName);
        } else if (measureAndSkillInApplication(BURST, selectedSkillName, selectedMeasureName)) {
            prepareDataForPopup(BURST, selectedSkillName);
        } else if (measureAndSkillInApplication(IDEL2, selectedSkillName, selectedMeasureName)) {
            prepareDataForPopup(IDEL2, selectedSkillName, selectedMeasureName);
        } else {
            // this is a nice way to get rid of d8 key but eslint does not know about it
            /* eslint-disable no-unused-vars */
            const { dibels8, ...nonD8Skills } = skillsStatusesByApplication;
            const selectedSkillStatus = flatten(values(nonD8Skills)).find(
                skill => skill.skillName === selectedSkillName,
            );
            if (selectedSkillStatus.accessLink) {
                navigateToPath(selectedSkillStatus.accessLink);
            }
        }
    };

    return (
        <div className='dashboard-additional-measures'>
            <div className='dashboard-additional-measures-container'>
                <div className='dashboard-additional-measures-title'>{localize(DROPDOWN_NAME)}</div>
                <Dropdown
                    controlClassName='dashboard-additional-measures-dropdown'
                    arrowClassName='dashboard-additional-measures-dropdown-arrow'
                    menuClassName={classNames('dashboard-additional-measures-dropdown-menu', {
                        'dashboard-espanol-menu': selectedAppGroup === ESPANOL,
                    })}
                    options={getDropdownOptions(skillsStatusesByApplication)}
                    value={localize(DROPDOWN_PLACEHOLDER)}
                    placeholder={localize(DROPDOWN_PLACEHOLDER)}
                    onChange={onSkillNameSelected}
                    ref={refCollection.get(student)}
                />
            </div>
        </div>
    );
};

PMMeasures.propTypes = {
    student: studentType.isRequired,
    skillsStatusesByApplication: PropTypes.shape({
        dibels8: PropTypes.arrayOf(
            PropTypes.shape({
                skillName: PropTypes.string,
                accessLink: PropTypes.string,
                hasPausedResult: PropTypes.bool,
                isRecommended: PropTypes.bool,
            }),
        ),
        r3d: PropTypes.arrayOf(
            PropTypes.shape({
                skillName: PropTypes.string,
                accessLink: PropTypes.string,
                hasPausedResult: PropTypes.bool,
                isRecommended: PropTypes.bool,
            }),
        ),
        burst: PropTypes.arrayOf(
            PropTypes.shape({
                skillName: PropTypes.string,
                accessLink: PropTypes.string,
                hasPausedResult: PropTypes.bool,
                isRecommended: PropTypes.bool,
            }),
        ),
    }).isRequired,
    nextFormUids: PropTypes.object.isRequired,
    hasInterventionInstruction: PropTypes.bool,
};

PMMeasures.defaultProps = {
    hasInterventionInstruction: false,
};

export default PMMeasures;
